import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from '../components/ui/command';
import { Layers, Check, X, Loader2, RefreshCw, ExternalLink, AlertTriangle, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VariantsPendingPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ total: 0 });
  
  // Variant options cache
  const [variantOptions, setVariantOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  
  // Selected variants (per entry)
  const [selectedVariants, setSelectedVariants] = useState({});
  const [confirmingVariant, setConfirmingVariant] = useState(null);
  
  // Popover open state (per entry)
  const [openPopovers, setOpenPopovers] = useState({});

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const [entriesRes, countsRes] = await Promise.all([
        axios.get(`${API_URL}/variants-pending`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/variants-pending/count`, { headers: getAuthHeader() })
      ]);
      setEntries(entriesRes.data);
      setCounts(countsRes.data);
    } catch (error) {
      toast.error('Failed to load variants pending entries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const fetchVariantOptions = async (entryId) => {
    if (variantOptions[entryId] || loadingOptions[entryId]) return;
    
    try {
      setLoadingOptions(prev => ({ ...prev, [entryId]: true }));
      const response = await axios.get(
        `${API_URL}/variants-pending/${entryId}/variant-options`,
        { headers: getAuthHeader() }
      );
      setVariantOptions(prev => ({ ...prev, [entryId]: response.data.options }));
    } catch (error) {
      toast.error('Failed to load variant options');
    } finally {
      setLoadingOptions(prev => ({ ...prev, [entryId]: false }));
    }
  };

  const handleVariantSelect = (entryId, itemId) => {
    const options = variantOptions[entryId] || [];
    const selected = options.find(o => o.item_id === itemId);
    
    setSelectedVariants(prev => ({
      ...prev,
      [entryId]: selected ? {
        item_id: selected.item_id,
        item_name: selected.item_name,
        price: selected.price,
        available: selected.available
      } : null
    }));
  };

  const handleConfirmVariant = async (entryId) => {
    const selected = selectedVariants[entryId];
    if (!selected) {
      toast.error('Please select a variant first');
      return;
    }
    
    try {
      setConfirmingVariant(entryId);
      
      const response = await axios.post(
        `${API_URL}/variants-pending/${entryId}/confirm`,
        {
          selected_item_id: selected.item_id,
          selected_item_name: selected.item_name,
          price: selected.price
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Variant confirmed successfully');
      
      // Remove from local state
      setEntries(prev => prev.filter(e => e.id !== entryId));
      setSelectedVariants(prev => {
        const updated = { ...prev };
        delete updated[entryId];
        return updated;
      });
      
      // Refresh counts
      const countsRes = await axios.get(`${API_URL}/variants-pending/count`, { headers: getAuthHeader() });
      setCounts(countsRes.data);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to confirm variant');
    } finally {
      setConfirmingVariant(null);
    }
  };

  const handleCancelEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to cancel this variant selection?')) return;
    
    try {
      await axios.delete(`${API_URL}/variants-pending/${entryId}`, { headers: getAuthHeader() });
      toast.success('Entry cancelled');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to cancel entry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  // Group entries by SO for better visualization
  const groupedEntries = entries.reduce((acc, entry) => {
    const soKey = entry.so_number;
    if (!acc[soKey]) {
      acc[soKey] = {
        so_id: entry.so_id,
        so_number: entry.so_number,
        client_name: entry.client_name,
        entries: []
      };
    }
    acc[soKey].entries.push(entry);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="variants-pending-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Variants Pending Register</h1>
          <p className="text-sm text-gray-500 mt-1">
            Work Orders with YTD (Yet to be Decided) variants awaiting selection
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="px-3 py-1 text-lg">
            {counts.total || 0} Pending
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchEntries}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Layers className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No pending variant selections</p>
            <p className="text-sm">All variants have been confirmed</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.values(groupedEntries).map((group) => (
            <Card key={group.so_number} className="overflow-hidden">
              {/* SO Header */}
              <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <a 
                    href={`/production/sales-orders?id=${group.so_id}`}
                    className="font-semibold text-blue-600 hover:underline flex items-center gap-1"
                  >
                    {group.so_number}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <span className="text-gray-500">|</span>
                  <span className="text-gray-700">{group.client_name}</span>
                </div>
                <Badge>{group.entries.length} variant(s) pending</Badge>
              </div>

              {/* Variants Table */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO No.</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Variant Group</TableHead>
                    <TableHead>Process</TableHead>
                    <TableHead>Select Variant</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.entries.map((entry) => {
                    const options = variantOptions[entry.id] || [];
                    const isLoading = loadingOptions[entry.id];
                    const selected = selectedVariants[entry.id];
                    
                    return (
                      <TableRow key={entry.id} data-testid={`variant-pending-row-${entry.id}`}>
                        <TableCell>
                          <a 
                            href={`/production/work-orders/${entry.wo_id}`}
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            {entry.wo_number}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </TableCell>
                        <TableCell className="font-medium">{entry.item_name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{entry.variant_group_name}</Badge>
                        </TableCell>
                        <TableCell className="text-gray-500">{entry.process_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Popover 
                              open={openPopovers[entry.id] || false} 
                              onOpenChange={(open) => {
                                setOpenPopovers(prev => ({ ...prev, [entry.id]: open }));
                                if (open) fetchVariantOptions(entry.id);
                              }}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={openPopovers[entry.id] || false}
                                  className="w-[280px] justify-between"
                                  data-testid={`variant-select-${entry.id}`}
                                >
                                  {selected ? (
                                    <span className="truncate">{selected.item_name}</span>
                                  ) : (
                                    <span className="text-muted-foreground">
                                      {isLoading ? "Loading..." : "Search & select variant..."}
                                    </span>
                                  )}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[350px] p-0" align="start">
                                <Command>
                                  <CommandInput placeholder="Search variant..." />
                                  <CommandList>
                                    <CommandEmpty>
                                      {isLoading ? "Loading variants..." : "No variant found."}
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {options.map(opt => (
                                        <CommandItem
                                          key={opt.item_id}
                                          value={opt.item_name}
                                          onSelect={() => {
                                            handleVariantSelect(entry.id, opt.item_id);
                                            setOpenPopovers(prev => ({ ...prev, [entry.id]: false }));
                                          }}
                                          className="cursor-pointer"
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              selected?.item_id === opt.item_id ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          <div className="flex items-center justify-between w-full">
                                            <span className="truncate flex-1">{opt.item_name}</span>
                                            <span className={`text-xs ml-2 ${opt.available <= 0 ? 'text-red-500' : 'text-green-600'}`}>
                                              ({opt.available})
                                            </span>
                                          </div>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            
                            {/* Stock Warning */}
                            {selected && selected.available <= 0 && (
                              <AlertTriangle className="w-4 h-4 text-amber-500" title="Low stock warning" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleConfirmVariant(entry.id)}
                              disabled={!selected || confirmingVariant === entry.id}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`confirm-variant-btn-${entry.id}`}
                            >
                              {confirmingVariant === entry.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-1" />
                                  Confirm
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelEntry(entry.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          ))}
        </div>
      )}
    </div>
    </DashboardLayout>
  );
};

export default VariantsPendingPage;
